import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { ReactTabulator,reactFormatter } from 'react-tabulator';
import { DatabaseContext } from "../../redux/DatabaseStore";
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { MdSearch } from "react-icons/md";
import { MdSearchOff } from "react-icons/md";
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, StepBackIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, CalendarIcon, EditRowIcon, RefreshIcon } from "../svg/svgIcons";
import { deleteRequest, getRequest,postRequest } from '../../api/ApiController';
import {useReactToPrint} from "react-to-print";
import EightDreport from "../print/EightDreport";
import { DateTime } from "luxon";
import AuthContext from "../../components/AuthContext";
import moment from "moment";
import { colorNameOrHexToHex } from '../../utils/moduleSettings';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import UtilContext from "../../components/UtilContext";
import { LocaleContext } from '../../components/LocaleContext';
import {
    buildPreviewPeriodOptionLabels,
    registerPreviewCalendarLocale,
} from '../../utils/previewUiLocale';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import { Chart } from 'primereact/chart';
import { Calendar } from '../../components/PrimeCalendar';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { addLocale } from 'primereact/api';
import {
    nextTrainingPreviewStatsState,
    resolveTrainingPreviewErrorMessage,
    shouldShowTrainingPreviewEmptyRecords,
    validateTrainingPreviewDateRange,
} from '../../utils/trainingPreviewStats';
import {
    PREVIEW_PERIOD_OPTIONS as PERIOD_OPTIONS,
    PREVIEW_PERIOD_DEFAULT,
    defaultDateForPeriod,
    datePickerPropsForPeriod,
    toLocalDateString,
} from '../../utils/previewPeriodSelection';
import { uiT } from '../../utils/uiTranslate';

const PREVIEW_PAGE = 'PreviewTraining';

function parseSavedPeriod(attrs) {
    if (!attrs?.preview_period || typeof attrs.preview_period?.timePeriod !== 'string') return null;
    if (!PERIOD_OPTIONS.includes(attrs.preview_period.timePeriod)) return null;
    const { timePeriod } = attrs.preview_period;
    const date = attrs.preview_period.date;
    if (timePeriod === "Všetko") return { timePeriod, date: null };
    if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
        return { timePeriod, date: date.map((d) => (d != null ? new Date(d) : null)) };
    }
    if (date != null) {
        const d = new Date(date);
        if (!isNaN(d.getTime())) return { timePeriod, date: d };
    }
    return null;
}

function serializePeriod(timePeriod, date) {
    const payload = { timePeriod };
    if (date == null) payload.date = null;
    else if (Array.isArray(date)) payload.date = date.map((d) => (d instanceof Date ? toLocalDateString(d) : d));
    else payload.date = date instanceof Date ? toLocalDateString(date) : date;
    return payload;
}

function getDateRange(timePeriod, date) {
    if (!date || timePeriod === "Všetko") return null;
    if (Array.isArray(date) && date.length >= 2) {
        return { dateFrom: new Date(date[0]), dateTo: new Date(date[1]) };
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    let dateFrom, dateTo;
    if (timePeriod === "Za deň") {
        dateFrom = new Date(d); dateFrom.setHours(0, 0, 0, 0);
        dateTo = new Date(d); dateTo.setHours(23, 59, 59, 999);
    } else if (timePeriod === 'Za mesiac') {
        dateFrom = new Date(d.getFullYear(), d.getMonth(), 1);
        dateTo = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    } else if (timePeriod === 'Za rok') {
        dateFrom = new Date(d.getFullYear(), 0, 1);
        dateTo = new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
    } else return null;
    return { dateFrom, dateTo };
}

export default function PreviewTraining() {

    const [state, dispatch] = useContext(DatabaseContext);
    const { setUserMaskFilter, toast } = useContext(UtilContext);
    const { t, locale } = useContext(LocaleContext);
    const periodOptionLabels = useMemo(() => buildPreviewPeriodOptionLabels(t), [t]);
    const [calendarLocale, setCalendarLocale] = useState(() => registerPreviewCalendarLocale(addLocale, locale, t));
    useEffect(() => { setCalendarLocale(registerPreviewCalendarLocale(addLocale, locale, t)); }, [locale, t]);

    const [timePeriod,setTimePeriod] = useState(PREVIEW_PERIOD_DEFAULT);
    const [date,setDate] = useState(null);
    const [dateProps,setDateProps] = useState({hidden:true});
    const appliedSavedRef = useRef(false);

    const range = useMemo(() => getDateRange(timePeriod, date), [timePeriod, date]);

    const [statsFromApi, setStatsFromApi] = useState(null);
    const [statsError, setStatsError] = useState(null);

    const periodStatsRef = useRef(null);
    const rangeRef = useRef(null);
    const statsFromApiRef = useRef(null);
    const statsErrorRef = useRef(null);
    periodStatsRef.current = statsFromApi?.periodStats ?? null;
    rangeRef.current = range;
    statsFromApiRef.current = statsFromApi;
    statsErrorRef.current = statsError;

    const trainingGraphColors = useMemo(() => {
        const ms = state.module_settings || [];
        const getVal = (key, defHex) => {
            const r = ms.find(s => s.module === 'training' && s.key === key);
            const v = r?.value ?? (key === 'trng_graph_0' ? 'red' : (key === 'trng_graph_ok' ? 'green' : 'orange'));
            return colorNameOrHexToHex(v, defHex);
        };
        return [
            getVal('trng_graph_0', '#dc2626'),
            getVal('trng_graph_15', '#ca8a04'),
            getVal('trng_graph_30', '#eab308'),
            getVal('trng_graph_ok', '#16a34a'),
        ];
    }, [state.module_settings]);

    const trainingColors2 = ['#15803d', '#b91c1c'];
    const certPeriodColors = ['#15803d', '#b91c1c'];

    

    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [statsLoading, setStatsLoading] = useState(true);

    const applyPreviewStatsState = (next) => {
        setStatsFromApi(next.stats);
        setStatsError(next.error);
        if (next.error) {
            const detail = resolveTrainingPreviewErrorMessage(next.error, t);
            toast?.current?.show({
                severity: 'error',
                summary: t('Common.Error'),
                detail,
                life: 4000,
            });
        }
    };

    useEffect(() => {
        let cancelled = false;
        const prev = { stats: statsFromApiRef.current, error: statsErrorRef.current };

        if (Array.isArray(date) && (date[0] == null || date[1] == null)) {
            const next = nextTrainingPreviewStatsState(prev, { range: { dateFrom: date[0], dateTo: date[1] } });
            if (!cancelled) {
                applyPreviewStatsState(next);
                setStatsLoading(false);
            }
            return () => { cancelled = true; };
        }

        const rangeCheck = validateTrainingPreviewDateRange(range?.dateFrom, range?.dateTo);
        if (!rangeCheck.ok) {
            const next = nextTrainingPreviewStatsState(prev, { range });
            if (!cancelled) {
                applyPreviewStatsState(next);
                setStatsLoading(false);
            }
            return () => { cancelled = true; };
        }

        setStatsLoading(true);

        const params = {};
        if (range?.dateFrom && range?.dateTo) {
            const dateFrom = toLocalDateString(range.dateFrom);
            const dateTo = toLocalDateString(range.dateTo);
            if (!dateFrom || !dateTo) {
                const invalid = nextTrainingPreviewStatsState(prev, { range: { dateFrom: 'invalid', dateTo: dateTo || 'invalid' } });
                if (!cancelled) {
                    applyPreviewStatsState(invalid);
                    setStatsLoading(false);
                }
                return () => { cancelled = true; };
            }
            params.date_from = dateFrom;
            params.date_to = dateTo;
        }
        const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
        getRequest('/api/training_preview_stats' + qs)
            .then((res) => {
                if (cancelled) return;
                applyPreviewStatsState(nextTrainingPreviewStatsState(prev, { response: res }));
            })
            .catch((reason) => {
                if (cancelled) return;
                applyPreviewStatsState(nextTrainingPreviewStatsState(prev, { rejected: reason }));
            })
            .finally(() => {
                if (!cancelled) setStatsLoading(false);
            });
        return () => { cancelled = true; };
    }, [range, refreshTrigger, date, t]);

    const inviteColors = ['#ca8a04', '#eab308', '#15803d'];

    const employeeTrainingData = useMemo(() => ({
        labels: [t('PreviewMaintenance.Text.Po_expiracii'), t('Common.DaysToExp15'), t('Common.DaysToExp30'), t('Common.Status.OK')],
        datasets: [{
            data: statsFromApi?.employee ?? [],
            backgroundColor: trainingGraphColors,
            hoverBackgroundColor: trainingGraphColors,
        }],
    }), [statsFromApi?.employee, trainingGraphColors]);

    const companyTrainingData = useMemo(() => ({
        labels: [t('PreviewMaintenance.Text.Po_expiracii'), t('Common.DaysToExp15'), t('Common.DaysToExp30'), t('Common.Status.OK')],
        datasets: [{
            data: statsFromApi?.company ?? [],
            backgroundColor: trainingGraphColors,
            hoverBackgroundColor: trainingGraphColors,
        }],
    }), [statsFromApi?.company, trainingGraphColors]);

    const invites = useMemo(() => ({
        labels: [t('PreviewTraining.Text.0_3_dni_do_skolenia'), t('PreviewTraining.Text.3_7_dni_do_skolenia'), t('PreviewTraining.Text.7_dni_a_viac_do_skolenia')],
        datasets: [{
            data: statsFromApi?.invites ?? [],
            backgroundColor: inviteColors,
            hoverBackgroundColor: inviteColors,
        }],
    }), [statsFromApi?.invites]);

    const periodStats = statsFromApi?.periodStats;

    const displayEmployeeData = useMemo(() => {
        if (periodStats) {
            return {
                labels: [t('Training.TrainingsOnTime'), t('PreviewTraining.Text.Skolenia_urobene_po_termine')],
                datasets: [{
                    data: [periodStats.trainingsDoneOnTime ?? 0, periodStats.trainingsDoneLate ?? 0],
                    backgroundColor: trainingColors2,
                    hoverBackgroundColor: trainingColors2,
                }],
            };
        }
        return employeeTrainingData;
    }, [periodStats, employeeTrainingData]);

    const displayInvitesData = useMemo(() => {
        if (periodStats) {
            return {
                labels: [t('PreviewTraining.Text.Vytvorene_pozvanky_za_obdobie')],
                datasets: [{
                    data: [periodStats.invitationsCreated ?? 0],
                    backgroundColor: ['#2563eb'],
                    hoverBackgroundColor: ['#2563eb'],
                }],
            };
        }
        return invites;
    }, [periodStats, invites]);

    const displayCompanyData = useMemo(() => {
        if (periodStats) {
            return {
                labels: [t('Maintenance.CertsUploadedOnTime'), t('PreviewMaintenance.Text.Certifikaty_nahrate_po_termine')],
                datasets: [{
                    data: [periodStats.certsOnTime ?? 0, periodStats.certsLate ?? 0],
                    backgroundColor: certPeriodColors,
                    hoverBackgroundColor: certPeriodColors,
                }],
            };
        }
        return companyTrainingData;
    }, [periodStats, companyTrainingData]);

    const [legendHidden, setLegendHidden] = useState({ employee: [], company: [], invites: [] });
    const originalDataRef = useRef({ employee: [], company: [], invites: [] });
    const originalTotal = (key, fallbackArr) => {
        // primárne používaj aktuálny dataset (fallbackArr),
        // aby celkový počet vždy sedel s grafom
        const base = (fallbackArr && fallbackArr.length) ? fallbackArr : originalDataRef.current[key];
        const src = base ?? [];
        return src.reduce((a, b) => a + (Number(b) || 0), 0);
    };
    const employeeChartRef = useRef(null);
    const companyChartRef = useRef(null);
    const invitesChartRef = useRef(null);

    const toggleLegendItem = (chartKey, index, chartRef) => () => {
        const ch = chartRef?.current?.getChart?.();
        if (!ch) return;
        const originals = originalDataRef.current[chartKey];
        if (!Array.isArray(originals)) return;
        const currentlyHidden = legendHidden[chartKey]?.[index];
        const nextHidden = !currentlyHidden;
        const hiddenArr = legendHidden[chartKey] ?? [];
        const newData = originals.map((val, i) => {
            const isHidden = i === index ? nextHidden : hiddenArr[i];
            return isHidden ? 0 : (Number(val) || 0);
        });
        const ds = ch.data.datasets[0];
        ds.data = [...newData];
        const meta = ch.getDatasetMeta?.(0);
        if (meta && Array.isArray(meta._parsed)) newData.forEach((v, i) => { meta._parsed[i] = v; });
        ch.update();
        setLegendHidden((prev) => {
            const arr = prev[chartKey] ?? [];
            const next = [...arr];
            next[index] = nextHidden;
            return { ...prev, [chartKey]: next };
        });
    };

    const handleRefresh = () => setRefreshTrigger((t) => t + 1);

    useEffect(() => {
        if (appliedSavedRef.current) return;
        if (!state.user_mask_filters?.length) return;
        appliedSavedRef.current = true;
        const mask = state.user_mask_filters.find((m) => m.page === PREVIEW_PAGE);
        const attrs = mask?.attributes;
        const parsed = typeof attrs === 'string' ? (() => { try { return parseSavedPeriod(JSON.parse(attrs)); } catch { return null; } })() : parseSavedPeriod(attrs);
        if (parsed) {
            setTimePeriod(parsed.timePeriod);
            setDate(parsed.date ?? defaultDateForPeriod(parsed.timePeriod));
            setDateProps(datePickerPropsForPeriod(parsed.timePeriod));
        }
    }, [state.user_mask_filters]);

    function changeTimePeriod(time_period) {
        setTimePeriod(time_period);
        setDate(defaultDateForPeriod(time_period));
        setDateProps(datePickerPropsForPeriod(time_period));
        setStatsLoading(true);
    }

    function showResult(dateVal) {
        setStatsLoading(true);
        setDate(dateVal);
    }

    const savePeriodToDb = () => {
        setUserMaskFilter(PREVIEW_PAGE, 'set_attributes', null, null, { preview_period: serializePeriod(timePeriod, date) });
    };


    useEffect(() => {
        const d = (src) => (src?.datasets?.[0]?.data ? [...src.datasets[0].data] : []);
        originalDataRef.current = {
            employee: d(displayEmployeeData),
            company: d(displayCompanyData),
            invites: d(displayInvitesData),
        };
    }, [displayEmployeeData, displayCompanyData, displayInvitesData]);

    const [employeeTrainingOptions] = useState({
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if (!item?.length) return;
            if (periodStatsRef.current && rangeRef.current) {
                const seg = item[0].index === 0 ? 'on_time' : 'late';
                const r = rangeRef.current;
                const dateFrom = toLocalDateString(r.dateFrom);
                const dateTo = toLocalDateString(r.dateTo);
                dispatch({ type: 'create_new_tab', name: t('TrainingCompanyCertStateList'), translationKey: 'TrainingCompanyCertStateList', legacyLabel: t('TrainingCompanyCertStateList'), page: 'TrainingStateList', props: { preview_period: { dateFrom, dateTo }, preview_segment: seg }, mask: 'trng016' });
                return;
            }
            if (item[0].index === 0) dispatch({ type: 'create_new_tab', name: t('TrainingCompanyCertStateList'), translationKey: 'TrainingCompanyCertStateList', legacyLabel: t('TrainingCompanyCertStateList'), page: 'TrainingStateList', props: { preview: 'after' }, mask: 'trng016' });
            else if (item[0].index === 1) dispatch({ type: 'create_new_tab', name: t('TrainingCompanyCertStateList'), translationKey: 'TrainingCompanyCertStateList', legacyLabel: t('TrainingCompanyCertStateList'), page: 'TrainingStateList', props: { preview: '15' }, mask: 'trng016' });
            else if (item[0].index === 2) dispatch({ type: 'create_new_tab', name: t('TrainingCompanyCertStateList'), translationKey: 'TrainingCompanyCertStateList', legacyLabel: t('TrainingCompanyCertStateList'), page: 'TrainingStateList', props: { preview: '30' }, mask: 'trng016' });
            else if (item[0].index === 3) dispatch({ type: 'create_new_tab', name: t('TrainingCompanyCertStateList'), translationKey: 'TrainingCompanyCertStateList', legacyLabel: t('TrainingCompanyCertStateList'), page: 'TrainingStateList', props: { preview: 'others' }, mask: 'trng016' });
        }
    });

    const [companyTrainingOptions] = useState({
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if (!item?.length) return;
            if (periodStatsRef.current && rangeRef.current) {
                const seg = item[0].index === 0 ? 'on_time' : 'late';
                const r = rangeRef.current;
                const dateFrom = toLocalDateString(r.dateFrom);
                const dateTo = toLocalDateString(r.dateTo);
                dispatch({ type: 'create_new_tab', name: t('PreviewTraining.Text.Certifikaty_skol_firiem'), translationKey: 'PreviewTraining.Text.Certifikaty_skol_firiem', legacyLabel: t('PreviewTraining.Text.Certifikaty_skol_firiem'), page: 'TrainingCompanyCertStateList', props: { preview_period: { dateFrom, dateTo }, preview_segment: seg }, mask: 'trng025' });
                return;
            }
            if (item[0].index === 0) dispatch({ type: 'create_new_tab', name: t('PreviewTraining.Text.Certifikaty_skol_firiem'), translationKey: 'PreviewTraining.Text.Certifikaty_skol_firiem', legacyLabel: t('PreviewTraining.Text.Certifikaty_skol_firiem'), page: 'TrainingCompanyCertStateList', props: { preview: 'after' }, mask: 'trng025' });
            else if (item[0].index === 1) dispatch({ type: 'create_new_tab', name: t('PreviewTraining.Text.Certifikaty_skol_firiem'), translationKey: 'PreviewTraining.Text.Certifikaty_skol_firiem', legacyLabel: t('PreviewTraining.Text.Certifikaty_skol_firiem'), page: 'TrainingCompanyCertStateList', props: { preview: '15' }, mask: 'trng025' });
            else if (item[0].index === 2) dispatch({ type: 'create_new_tab', name: t('PreviewTraining.Text.Certifikaty_skol_firiem'), translationKey: 'PreviewTraining.Text.Certifikaty_skol_firiem', legacyLabel: t('PreviewTraining.Text.Certifikaty_skol_firiem'), page: 'TrainingCompanyCertStateList', props: { preview: '30' }, mask: 'trng025' });
            else if (item[0].index === 3) dispatch({ type: 'create_new_tab', name: t('PreviewTraining.Text.Certifikaty_skol_firiem'), translationKey: 'PreviewTraining.Text.Certifikaty_skol_firiem', legacyLabel: t('PreviewTraining.Text.Certifikaty_skol_firiem'), page: 'TrainingCompanyCertStateList', props: { preview: 'others' }, mask: 'trng025' });
        }
    });

    const [invitesOptions] = useState({
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if (!item?.length) return;
            if (periodStatsRef.current && rangeRef.current) {
                const r = rangeRef.current;
                const dateFrom = toLocalDateString(r.dateFrom);
                const dateTo = toLocalDateString(r.dateTo);
                dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.FavoriteTitle'), translationKey: 'TrainingInvitationList.FavoriteTitle', legacyLabel: t('TrainingInvitationList.FavoriteTitle'), page: 'TrainingInvitationList', props: { preview_period: { dateFrom, dateTo } }, mask: 'trng026' });
                return;
            }
            if (item[0].index === 0) dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.FavoriteTitle'), translationKey: 'TrainingInvitationList.FavoriteTitle', legacyLabel: t('TrainingInvitationList.FavoriteTitle'), page: 'TrainingInvitationList', props: { preview: '03' }, mask: 'trng026' });
            else if (item[0].index === 1) dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.FavoriteTitle'), translationKey: 'TrainingInvitationList.FavoriteTitle', legacyLabel: t('TrainingInvitationList.FavoriteTitle'), page: 'TrainingInvitationList', props: { preview: '37' }, mask: 'trng026' });
            else if (item[0].index === 2) dispatch({ type: 'create_new_tab', name: t('TrainingInvitationList.FavoriteTitle'), translationKey: 'TrainingInvitationList.FavoriteTitle', legacyLabel: t('TrainingInvitationList.FavoriteTitle'), page: 'TrainingInvitationList', props: { preview: '7m' }, mask: 'trng026' });
        }
    });

    const plugin = {
        id: 'emptyDoughnut',
        afterDraw(chart) {
            if (!shouldShowTrainingPreviewEmptyRecords(statsErrorRef.current, statsFromApiRef.current)) return;
            const data = chart.data?.datasets?.[0]?.data ?? [];
            const count = data.reduce((a, b) => a + (b ?? 0), 0);
            if (count < 1) {
                const ctx = chart.ctx;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.font = '20px Arial';
                ctx.fillStyle = '#9ca3af';
                ctx.fillText(t('Common.NoRecords'), chart.width / 2, chart.height / 2);
            }
        },
    };

    const showPreviewCharts = !statsLoading && statsFromApi != null;
    const statsErrorText = statsError ? resolveTrainingPreviewErrorMessage(statsError, t) : '';

    const legendEl = (labels, colors, chartKey, chartRef, hiddenArr = []) => (
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
            {labels?.map((label, i) => (
                chartRef != null ? (
                    <button key={i} type="button" onClick={toggleLegendItem(chartKey, i, chartRef)} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: hiddenArr[i] ? 'line-through' : undefined, opacity: hiddenArr[i] ? 0.5 : 1 }}>
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colors?.[i] ?? '#6b7280' }} />
                        {label}
                    </button>
                ) : (
                    <span key={i} className="inline-flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colors?.[i] ?? '#6b7280' }} />
                        {label}
                    </span>
                )
            ))}
        </div>
    );

    return (
        <div className="min-h-[calc(100vh-137px)]">
        <div className="lg:grid grid-cols-3 gap-1">
            <div></div>
            <div>
                <div className="flex items-center gap-3 mt-4">
                    <label htmlFor="time_period" className="text-xl font-semibold text-gray-800 shrink-0">{t('Common.PeriodLabel')}</label>
                    <select
                        name="time_period"
                        id="time_period"
                        value={timePeriod}
                        onChange={(e) => changeTimePeriod(e.target.value)}
                        className="bg-white flex-1 min-w-0 px-4 py-2 text-gray-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    >
                        <option value="Všetko">{periodOptionLabels['Všetko']}</option>
                        <option value="Za deň">{periodOptionLabels['Za deň']}</option>
                        <option value="Za mesiac">{periodOptionLabels['Za mesiac']}</option>
                        <option value="Za rok">{periodOptionLabels['Za rok']}</option>
                        <option value="Vlastné">{periodOptionLabels['Vlastné']}</option>
                    </select>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        title={t('Common.RefreshData')}
                        className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 text-white transition-colors"
                    >
                        <RefreshIcon />
                    </button>
                    <button
                        type="button"
                        onClick={savePeriodToDb}
                        title={t('ServiceTaskPreview.SavePeriodHint')}
                        className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
                    >
                        {t('Common.SaveMask')}
                    </button>
                </div>
            </div>
            <div></div>
            <div></div>
            <div className="flex-auto">
                <Calendar
                    id="buttondisplay"
                    style={{ border: '1px solid #e5e7eb', transition: 'background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s', borderRadius: '6px' }}
                    value={date}
                    onChange={(e) => showResult(e.target.value)}
                    showIcon
                    className="w-full"
                    visible={true}
                    locale={calendarLocale}
                    {...dateProps}
                    hideOnRangeSelection
                />
            </div>
            <div></div>
        </div>
        {statsErrorText && (
            <div className="px-4 pt-3">
                <Message severity="error" text={statsErrorText} />
            </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
          <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
            <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewTraining.Trainings')}</h2>
            <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
              {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : showPreviewCharts ? <Chart ref={employeeChartRef} type="doughnut" data={displayEmployeeData} options={employeeTrainingOptions} className="w-full h-full" plugins={[plugin]} /> : null}
            </div>
            {showPreviewCharts && legendEl(displayEmployeeData.labels, displayEmployeeData.datasets?.[0]?.backgroundColor, 'employee', employeeChartRef, legendHidden.employee)}
            <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : showPreviewCharts ? t('Common.TotalCount', { count: originalTotal('employee', displayEmployeeData?.datasets?.[0]?.data) }) : null}</div>
          </div>
          <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
            <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewTraining.Invitations')}</h2>
            <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
              {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : showPreviewCharts ? <Chart ref={invitesChartRef} type="doughnut" data={displayInvitesData} options={invitesOptions} className="w-full h-full" plugins={[plugin]} /> : null}
            </div>
            {showPreviewCharts && legendEl(displayInvitesData.labels, displayInvitesData.datasets?.[0]?.backgroundColor, 'invites', invitesChartRef, legendHidden.invites)}
            <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : showPreviewCharts ? t('Common.TotalCount', { count: originalTotal('invites', displayInvitesData?.datasets?.[0]?.data) }) : null}</div>
          </div>
          <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
            <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewTraining.CompanyCertificates')}</h2>
            <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
              {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : showPreviewCharts ? <Chart ref={companyChartRef} type="doughnut" data={displayCompanyData} options={companyTrainingOptions} className="w-full h-full" plugins={[plugin]} /> : null}
            </div>
            {showPreviewCharts && legendEl(displayCompanyData.labels, displayCompanyData.datasets?.[0]?.backgroundColor, 'company', companyChartRef, legendHidden.company)}
            <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : showPreviewCharts ? t('Common.TotalCount', { count: originalTotal('company', displayCompanyData?.datasets?.[0]?.data) }) : null}</div>
          </div>
        </div>
</div>
    );
}
